'use client'

import { motion } from 'motion/react'
import { useMemo, useState } from 'react'
import { useMeasure } from 'react-use'
import { useGetRestaurantReviewsQuery } from '@/api/restaurant/get-restaurant-reviews-query'
import { Button } from '@/components/button'
import { useRestaurantDetailContext } from '@/features/restaurants/restaurant-detail/restaurant-detail-context'
import { RestaurantDetailSection } from '@/features/restaurants/restaurant-detail/restaurant-detail-section'
import { RestaurantDetailSectionId } from '@/features/restaurants/restaurant-detail/utils/restaurant-detail-helpers'
import { CustomerReview } from '@/features/reviews/cutomer-review'
import { Rating } from '@/features/reviews/rating'

const DEFAULT_NUMBER_OF_REVIEWS_SHOWN = 5

export const RestaurantDetailReviews = () => {
    const { restaurant } = useRestaurantDetailContext()

    const [isExpanded, setIsExpanded] = useState(false)

    const [containerRef, { height: containerHeight }] = useMeasure<HTMLDivElement>()

    const { data } = useGetRestaurantReviewsQuery({
        restaurantSlug: restaurant?.slug ?? '',
        page: 1,
    })

    const allReviews = data?.data

    const formattedReviews = useMemo(() => {
        if (!restaurant?.reviews) return

        const shownReviews = isExpanded && allReviews ? allReviews : restaurant.reviews

        return [...shownReviews]
            .sort(
                (a, b) => new Date(b.dateTimestamp).getTime() - new Date(a.dateTimestamp).getTime()
            )
            .map((review) => {
                // As there is no title, we cut the first 4 words from the body and add an ellipsis

                return {
                    name: review.name || '',
                    rating: review.rating,
                    body: review.review ?? undefined,
                    dateTime: review.dateTimestamp as unknown as string,
                }
            })
    }, [restaurant, allReviews, isExpanded])

    const writtenReviewsCount = restaurant ? restaurant.reviewsCount : undefined

    const showExpandButton = useMemo(() => {
        return Boolean(writtenReviewsCount && writtenReviewsCount > DEFAULT_NUMBER_OF_REVIEWS_SHOWN)
    }, [writtenReviewsCount])

    if (!restaurant || typeof restaurant.avgRating !== 'number') return null

    return (
        <RestaurantDetailSection
            id={RestaurantDetailSectionId.REVIEWS}
            title={'restaurants.detail.reviews.title'}
            description={'restaurants.detail.reviews.description'}
        >
            <Rating
                rating={restaurant.avgRating}
                ratingCount={restaurant.ratingsCount}
                writtenRatingCount={restaurant.reviewsCount}
                starsStyleType="maximal"
                ratingCountStyleType="count-and-label"
                starClassName="w-[35px] h-[35px]"
                starsClassName="top-[-2.5px]"
                ratingClassName="text-h1"
                labelClassName="text-fg-secondary text-p-sm"
                containerClassName="flex-col w-fit"
            />

            {Boolean(formattedReviews?.length) && (
                <motion.div
                    initial={{ height: '100%' }}
                    animate={{ height: containerHeight || '100%' }}
                    className="mt-8 md:mt-8 w-full overflow-hidden"
                >
                    <div ref={containerRef} className="flex flex-col w-full gap-8">
                        {formattedReviews?.map((review, index) => {
                            const shouldRender =
                                isExpanded || index < DEFAULT_NUMBER_OF_REVIEWS_SHOWN - 1

                            if (!shouldRender) return null

                            return (
                                <CustomerReview
                                    type="full"
                                    review={review}
                                    key={'restaurant-detail-reviews' + index}
                                />
                            )
                        })}
                    </div>
                </motion.div>
            )}

            {showExpandButton && (
                <div className="flex items-center justify-center w-full py-8">
                    <Button
                        shape="pill"
                        styleType="secondary"
                        onClick={() => setIsExpanded((prev) => !prev)}
                    >
                        {isExpanded ? 'Show less' : 'Show all reviews'}
                    </Button>
                </div>
            )}
        </RestaurantDetailSection>
    )
}
